/**
 * LA FINESTRA DI STAMPA: un foglio HTML mandato alla stampante del browser.
 *
 * Serve a chi stampa dal sito -- il palinsesto di uno sport, la giocata, la
 * prenotazione -- e fa una cosa sola: prende la pagina gia' composta e la
 * passa al dialogo di stampa del sistema. Niente ESC/POS, niente
 * stampanti dirette: quello che esce lo decide il driver di chi stampa.
 *
 * DA UN TELAIO NASCOSTO e non da una finestra nuova: le finestre nuove le
 * bloccano i browser, e l'utente vedrebbe solo non succedere nulla. Il telaio
 * sta fuori dallo schermo (a sinistra di diecimila punti) ed e' LARGO QUANTO
 * LA CARTA: 210 mm, 80 mm o 58 mm. Prima era di zero per zero, e una pagina
 * larga zero non si puo' misurare.
 *
 * TRE COSE PRIMA DI STAMPARE
 *   1. Le immagini (il logo, A4 o rotolo): si aspetta che siano pronte, ma non
 *      piu' di due secondi. Un logo che non arriva si toglie e al suo posto
 *      si scrive il testo di `data-alt` (il dominio): meglio un nome scritto
 *      che un riquadro vuoto o una stampa che non parte.
 *   2. L'altezza del rotolo: un foglio da 80 mm non ha un'altezza, e Chrome
 *      taglierebbe una giocata lunga in tanti pezzi alti come un A4. Si misura
 *      la pagina e si scrive @page{size:80mm <altezza>mm}.
 *   3. Il telaio si toglie dopo la stampa (afterprint), o dopo un minuto se
 *      il browser non lo dice. Prima si toglieva dopo un secondo, e Safari
 *      stampava un telaio gia' morto.
 *
 * SU iPHONE E iPAD il telaio non si stampa: Safari ha la cattiva abitudine di
 * stampare la pagina di sotto invece del telaio. Li' la pagina si misura lo
 * stesso nel telaio, poi si copia in un contenitore della pagina vera che
 * esiste solo in stampa (#lvc-solo-stampa), e si stampa la finestra intera.
 * DA PROVARE su un iPhone vero, rotolo e A4, prima di annunciarlo: qui non
 * c'e' un WebKit per farlo.
 *
 * COSA NON FA: non compone la pagina (la scrivono stampa.js, stampaGiocata.js)
 * e non sa se la stampa e' davvero uscita: il browser non lo dice.
 */

const LARGHEZZE = {A4: '210mm', ROTOLO_80: '80mm', ROTOLO_58: '58mm'};
const ATTESA_IMMAGINI = 2000;
const RIPIEGO_RIMOZIONE = 60000;
const ID_SOLO_STAMPA = 'lvc-solo-stampa';

/** Un testo dentro una pagina HTML, anche dentro un attributo: & < > " ' scritti come entita'. */
export function pulisci(testo) {
  return String(testo == null ? '' : testo)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
}

/** Il rotolo: 80 o 58 mm, senza un'altezza sua. */
function eRotolo(formato) {
  return formato === 'ROTOLO_80' || formato === 'ROTOLO_58';
}

/** iPhone, iPod, iPad (anche l'iPad che si dice Mac, ma col dito). */
export function eIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  if (/iP(hone|ad|od)/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && Number(navigator.maxTouchPoints) > 1;
}

/** Dai punti dello schermo ai millimetri della carta, con 6 mm di margine sotto per il taglio. */
export function altezzaInMm(punti) {
  return Math.ceil((Number(punti) || 0) * 25.4 / 96 + 6);
}

function attesa(ms) {
  return new Promise((risolvi) => setTimeout(risolvi, ms));
}

/* Un'immagine e' pronta quando decode() risponde; dove decode() non c'e' si
   ascoltano load ed error. In tutti e due i casi non si aspetta piu' di due
   secondi: dopo, l'immagine conta come persa. */
function prontaOPersa(img) {
  let pronta;
  if (typeof img.decode === 'function') {
    pronta = img.decode().then(() => true, () => false);
  } else if (img.complete) {
    pronta = Promise.resolve(img.naturalWidth > 0);
  } else {
    pronta = new Promise((risolvi) => {
      img.addEventListener('load', () => risolvi(true), {once: true});
      img.addEventListener('error', () => risolvi(false), {once: true});
    });
  }
  return Promise.race([pronta, attesa(ATTESA_IMMAGINI).then(() => false)]);
}

async function aspettaLeImmagini(doc) {
  const immagini = Array.from(doc.images || []);
  await Promise.all(immagini.map(async (img) => {
    const ok = await prontaOPersa(img);
    if (ok) return;
    const testo = img.getAttribute('data-alt') || '';
    if (testo) {
      const scritto = doc.createElement('span');
      scritto.className = 'senza-immagine';
      scritto.textContent = testo;
      img.replaceWith(scritto);
    } else {
      img.remove();
    }
  }));
}

/* Il rotolo e' alto quanto quello che c'e' scritto: si misura e si dice alla
   stampante, altrimenti esce a pezzi di A4. */
function misuraIlRotolo(doc, formato) {
  const corpo = doc.body;
  const punti = Math.max(corpo ? corpo.scrollHeight : 0,
      doc.documentElement ? doc.documentElement.scrollHeight : 0);
  const stile = doc.createElement('style');
  stile.setAttribute('data-pagina', '');
  stile.textContent = '@page{size:' + LARGHEZZE[formato] + ' ' + altezzaInMm(punti) + 'mm;margin:0}';
  (doc.head || doc.documentElement).appendChild(stile);
}

function telaioNascosto(larghezza) {
  const telaio = document.createElement('iframe');
  telaio.setAttribute('aria-hidden', 'true');
  telaio.setAttribute('tabindex', '-1');
  Object.assign(telaio.style, {
    position: 'fixed', left: '-10000px', top: '0',
    width: larghezza, height: '10px', border: '0',
  });
  document.body.appendChild(telaio);
  return telaio;
}

function caricato(telaio) {
  const doc = telaio.contentWindow.document;
  if (doc.readyState === 'complete') return Promise.resolve();
  return new Promise((risolvi) => {
    /* Un telaio scritto a mano non sempre manda load: dopo un attimo si va lo stesso. */
    const ripiego = setTimeout(risolvi, ATTESA_IMMAGINI);
    telaio.addEventListener('load', () => { clearTimeout(ripiego); risolvi(); }, {once: true});
  });
}

/* Togliere dopo la stampa: afterprint quando il browser lo manda, se no dopo
   un minuto. Una volta sola. */
function toglieDopo(finestra, togli) {
  let fatto = false;
  const una = () => {
    if (fatto) return;
    fatto = true;
    try { finestra.removeEventListener('afterprint', una); } catch (e) { /* telaio gia' via */ }
    togli();
  };
  try { finestra.addEventListener('afterprint', una); } catch (e) { /* niente afterprint */ }
  setTimeout(una, RIPIEGO_RIMOZIONE);
}

/* iOS: la pagina gia' misurata va nella finestra vera, in un contenitore che
   si vede solo in stampa. Gli stili della pagina valgono solo in stampa, cosi'
   lo schermo non cambia. */
function stampaNellaPagina(doc) {
  const vecchio = document.getElementById(ID_SOLO_STAMPA);
  if (vecchio) vecchio.remove();
  const stili = Array.from(doc.querySelectorAll('style')).map((s) => s.textContent).join('\n');
  const contenitore = document.createElement('div');
  contenitore.id = ID_SOLO_STAMPA;
  contenitore.setAttribute('aria-hidden', 'true');
  const stile = document.createElement('style');
  stile.textContent = '#' + ID_SOLO_STAMPA + '{display:none}'
    + '@media print{body>*:not(#' + ID_SOLO_STAMPA + '){display:none!important}'
    + '#' + ID_SOLO_STAMPA + '{display:block}' + stili + '}';
  const corpo = document.createElement('div');
  corpo.className = (doc.body && doc.body.className) || '';
  corpo.innerHTML = doc.body ? doc.body.innerHTML : '';
  contenitore.appendChild(stile);
  contenitore.appendChild(corpo);
  document.body.appendChild(contenitore);
  toglieDopo(window, () => contenitore.remove());
  window.print();
}

/* LA PAGINA TORNA DOV'ERA. Su Safari, stampando dal telaio e poi annullando,
   la pagina restava scesa di qualche decina di punti: la testata fissa
   copriva «Menu sport» e la riga col nome del campionato, e con la rotella
   sopra le colonne non si risaliva (scorrono loro, non la pagina): sembrava
   bloccata. Si ricorda dov'era prima di stampare e ci si torna quando la
   finestra di stampa si chiude, comunque si chiuda: Chrome aspetta dentro
   print(), Safari torna subito e la chiude dopo. Per mezzo minuto si rimette
   a posto anche quando la pagina riprende il fuoco o finisce la stampa.
   (Portato qui dalla stampa dello sport, main #124, perche' valga per tutte.) */
const RIMETTI_PER = 30000;

function ricordaDovEra() {
  const x = window.scrollX;
  const y = window.scrollY;
  const rimetti = () => {
    if (window.scrollX !== x || window.scrollY !== y) window.scrollTo(x, y);
  };
  const dopo = () => [0, 150, 500].forEach((ms) => setTimeout(rimetti, ms));
  return function tornaDovEra(finestra) {
    dopo();
    const altre = [window, finestra].filter(Boolean);
    window.addEventListener('focus', dopo);
    altre.forEach((f) => {
      try { f.addEventListener('afterprint', dopo); } catch (e) { /* finestra gia' via */ }
    });
    setTimeout(() => {
      window.removeEventListener('focus', dopo);
      altre.forEach((f) => {
        try { f.removeEventListener('afterprint', dopo); } catch (e) { /* finestra gia' via */ }
      });
    }, RIMETTI_PER);
  };
}

/**
 * Stampa una pagina HTML intera (<!DOCTYPE html>…).
 *
 * @param {string} html  la pagina, gia' pulita (pulisci) dove serve
 * @param {{formato?: 'A4'|'ROTOLO_80'|'ROTOLO_58'}} opzioni
 * @returns {Promise<void>} quando il dialogo di stampa e' stato chiesto
 */
export async function stampaPagina(html, {formato = 'A4'} = {}) {
  const carta = LARGHEZZE[formato] ? formato : 'A4';
  const telaio = telaioNascosto(LARGHEZZE[carta]);
  const dentro = telaio.contentWindow;
  dentro.document.open();
  dentro.document.write(html);
  dentro.document.close();

  try {
    await caricato(telaio);
    const doc = dentro.document;
    await aspettaLeImmagini(doc);
    if (eRotolo(carta)) misuraIlRotolo(doc, carta);

    const tornaDovEra = ricordaDovEra();
    if (eIOS()) {
      try {
        stampaNellaPagina(doc);
      } finally {
        tornaDovEra(null);
      }
      telaio.remove();
      return;
    }
    toglieDopo(dentro, () => telaio.remove());
    try {
      dentro.focus();
      dentro.print();
    } finally {
      tornaDovEra(dentro);
    }
  } catch (errore) {
    telaio.remove();
    throw errore;
  }
}
